import React from 'react';
import { Building2, Users, Handshake, TrendingUp } from 'lucide-react';

export const Partners = () => {
  const partnerTypes = [
    { icon: Building2, label: 'Government Entities' },
    { icon: Users, label: 'Semi-Government Organizations' },
    { icon: Handshake, label: 'Private Enterprises' },
    { icon: TrendingUp, label: 'Technology Partners' }
  ];

  // Client logos
  const clientLogos = [
    { name: 'Ministry of Sport', logo: 'https://customer-assets.emergentagent.com/job_revotech-preview/artifacts/q8dwijiu_MoS.png' },
    { name: 'Saudi Arabian National Water Company', logo: 'https://customer-assets.emergentagent.com/job_revotech-preview/artifacts/ozmkb2yu_NWC.png' },
    { name: 'Ministry of Education', logo: 'https://customer-assets.emergentagent.com/job_revotech-preview/artifacts/l07axlpb_MoE.png' },
    { name: 'Human Resources Development Fund', logo: 'https://customer-assets.emergentagent.com/job_revotech-preview/artifacts/u8al61zj_HRDF.png' },
    { name: 'Snabil', logo: 'https://customer-assets.emergentagent.com/job_revotech-preview/artifacts/ykkchu9h_Snabil.png' },
  ];

  // Duplicate logos for seamless infinite scroll
  const duplicatedLogos = [...clientLogos, ...clientLogos];

  return (
    <section id="partners" className="py-16 md:py-32 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#5B42FF]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-xs md:text-sm font-semibold text-[#5B42FF] uppercase tracking-wider mb-3 md:mb-4">Success Partners</h2>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            Collaborative Excellence
          </h3>
          <div className="w-20 md:w-24 h-1 bg-[#5B42FF] mx-auto mb-6 md:mb-8"></div>
          <p className="text-base md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed px-4">
            We collaborate closely with our partners to deliver high-quality, scalable solutions aligned with organizational and national objectives.
          </p>
        </div>

        {/* Partner Types */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-12 md:mt-16">
          {partnerTypes.map((partner, index) => (
            <div
              key={index}
              className="group p-6 md:p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl md:rounded-2xl hover:border-[#5B42FF]/50 transition-all duration-300 hover:transform hover:scale-105 text-center"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#5B42FF]/20 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 mx-auto group-hover:bg-[#5B42FF]/30 transition-all duration-300 group-hover:scale-110">
                <partner.icon className="w-6 h-6 md:w-8 md:h-8 text-[#5B42FF]" />
              </div>
              <p className="text-sm md:text-base text-white/90 font-medium">{partner.label}</p>
            </div>
          ))}
        </div>

        {/* Client Logos Section - Infinite Scroll Animation */}
        {clientLogos.length > 0 && (
          <div className="mt-16 md:mt-24">
            <h4 className="text-xl md:text-2xl font-bold text-white text-center mb-8 md:mb-12">Trusted By</h4>
            
            {/* Infinite scroll container */}
            <div className="relative overflow-hidden">
              {/* Gradient overlays for fade effect */}
              <div className="absolute left-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-r from-black to-transparent z-10"></div>
              <div className="absolute right-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-l from-black to-transparent z-10"></div>
              
              {/* Scrolling logos */}
              <div className="flex animate-scroll">
                {duplicatedLogos.map((client, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 mx-6 md:mx-8 py-6 md:py-8"
                  >
                    <div className="group p-4 md:p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl hover:border-[#5B42FF]/50 transition-all duration-300 hover:transform hover:scale-105 flex items-center justify-center min-w-[140px] md:min-w-[180px] h-[80px] md:h-[100px]">
                      <img
                        src={client.logo}
                        alt={client.name}
                        className="max-h-12 md:max-h-16 w-auto opacity-70 group-hover:opacity-100 transition-opacity duration-300 filter grayscale group-hover:grayscale-0"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Statement */}
        <div className="mt-12 md:mt-16 text-center px-4">
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Our ecosystem enables impactful and future-ready outcomes through strategic partnerships and collaborative innovation.
          </p>
        </div>
      </div>

      {/* CSS for infinite scroll animation */}
      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};